import type { Auction } from "@/services/types";
import { Badge } from "@/components/base/Badge";
import { CurrencyDisplay } from "@/components/currency/CurrencyDisplay";
import { formatDate } from "@/utils/format";

interface HeaderBandProps {
  auction: Auction;
  pool: number;
  currentLowest: number | null;
  currentInstallment: number;
}

/**
 * The focused header band for the auction console. The pool, the current lowest
 * bid and the resulting per-member installment are the three numbers announced
 * aloud in the room — they get the clearest visual priority on the screen.
 */
export function HeaderBand({ auction, pool, currentLowest, currentInstallment }: HeaderBandProps) {
  return (
    <div className="mb-4 rounded-lg border border-background-200 bg-background-50 p-4">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-lg font-semibold text-foreground-950">{auction.groupName}</h2>
            <span className="tnum text-xs text-foreground-400">{auction.groupCode}</span>
            {auction.isOrganizerReserved ? (
              <Badge color="accent" dot>Reserved organizer cycle</Badge>
            ) : (
              <Badge variant="active" dot>Auction</Badge>
            )}
          </div>
          <p className="mt-1 text-sm text-foreground-600">
            <span className="tnum font-medium">{auction.periodLabel}</span>
            <span className="mx-2 text-foreground-300">·</span>
            Cycle {auction.cycle} of {auction.memberCount}
            <span className="mx-2 text-foreground-300">·</span>
            {formatDate(auction.scheduledDate)}
            <span className="mx-2 text-foreground-300">·</span>
            {auction.isOrganizerReserved
              ? `Organizer: ${auction.organizer?.fullName ?? "—"}`
              : `${auction.eligible.length} member${auction.eligible.length === 1 ? "" : "s"} eligible`}
          </p>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="rounded-md bg-background-100 px-4 py-3">
          <p className="text-xs font-medium uppercase tracking-wide text-foreground-500">Pool this month</p>
          <p className="tnum mt-1 text-2xl font-bold leading-none text-foreground-950">
            <CurrencyDisplay amount={pool} hideSymbol />
          </p>
          <p className="tnum mt-1 text-xs text-foreground-500">{auction.memberCount} × {auction.monthlyInstallment.toLocaleString("en-IN")}</p>
        </div>
        <div className="rounded-md bg-accent-100 px-4 py-3">
          <p className="text-xs font-medium uppercase tracking-wide text-accent-800">Current lowest bid</p>
          <p className="tnum mt-1 text-2xl font-bold leading-none text-accent-800">
            {currentLowest != null ? <CurrencyDisplay amount={currentLowest} hideSymbol /> : <span className="text-accent-400">No bid yet</span>}
          </p>
          <p className="tnum mt-1 text-xs text-accent-700">{currentLowest != null ? "Standing leader" : "Waiting for the first bid"}</p>
        </div>
        <div className="rounded-md bg-secondary-100 px-4 py-3">
          <p className="text-xs font-medium uppercase tracking-wide text-secondary-800">Net installment / member</p>
          <p className="tnum mt-1 text-2xl font-bold leading-none text-secondary-900">
            <CurrencyDisplay amount={currentInstallment} hideSymbol />
          </p>
          <p className="tnum mt-1 text-xs text-secondary-700">
            {auction.monthlyInstallment - currentInstallment > 0
              ? `Dividend ${(auction.monthlyInstallment - currentInstallment).toLocaleString("en-IN")} applied`
              : "Full installment this month"}
          </p>
        </div>
      </div>
    </div>
  );
}